/**
 * authStallVital — how a page says its sign-in start was held up, and by what (2026-10-03).
 *
 * Firebase checks its own browser database when it boots. While another prest0 tab has a delete of that database
 * waiting (the sign-out wipe of an older build did that), every later open of it queues behind the delete, and the
 * page's sign-in start waits until every prest0 tab is closed: Justin's stuck "getting your threads back…". The
 * startup watch (authStartupWatch.ts) stamps what it found once per page load as a User Timing mark, and the
 * first-party web-vitals beacon (src/reportWebVitals.ts) sends it as one more row: a RUNG from the fixed list below
 * and how long the page waited for sign-in to answer. Nothing else.
 *
 * A zero-import leaf on purpose: the reporter is on the first download of every page. The server's allow-list
 * (functions/src/telemetry/webVitalsSchema.ts) carries the same literals; tests/auth/authStartupWatch.test.ts pins
 * the pair.
 */

/** The User Timing mark the watch stamps once per page load. */
export const AUTH_STALL_MARK = 'prest0:auth-stall';
/** Stamped once, when sign-in first answers (who is here, or that nobody is). */
export const AUTH_ANSWERED_MARK = 'prest0:auth-answered';

/** The metric name the beacon sends the mark under. */
export const AUTH_STALL_METRIC = 'AUTH_STALL';

/**
 * Every outcome the watch can report, and nothing else.
 *   auth-db       Firebase's sign-in database could not be opened in time (another tab's pending delete)
 *   heartbeat-db  Firebase's heartbeat database could not be opened in time
 *   unheld        sign-in had not answered, but both databases opened: something else (the network) was slow
 */
export const AUTH_STALL_RUNGS = ['auth-db', 'heartbeat-db', 'unheld'] as const;

export type AuthStallRung = (typeof AUTH_STALL_RUNGS)[number];

/** The longest wait a row carries (the server's own ceiling for any value). */
export const AUTH_STALL_MAX_MS = 120_000;

type MarkingPerformance = Performance & {
  mark?: Performance['mark'];
  getEntriesByName?: Performance['getEntriesByName'];
};

function perf(): MarkingPerformance | null {
  try {
    return typeof performance !== 'undefined' ? (performance as MarkingPerformance) : null;
  } catch {
    return null;
  }
}

function hasMark(name: string): boolean {
  try {
    return (perf()?.getEntriesByName?.(name, 'mark')?.length ?? 0) > 0;
  } catch {
    return false;
  }
}

/** Stamp what the watch found, once per page load. Nothing here can throw into the page. */
export function stampAuthStall(rung: AuthStallRung): void {
  if (hasMark(AUTH_STALL_MARK)) return;
  try {
    perf()?.mark?.(AUTH_STALL_MARK, { detail: { rung } });
  } catch {
    /* measurement must never affect the page it measures */
  }
}

/** Stamp the moment sign-in first answered, once per page load. */
export function stampAuthAnswered(): void {
  if (hasMark(AUTH_ANSWERED_MARK)) return;
  try {
    perf()?.mark?.(AUTH_ANSWERED_MARK);
  } catch {
    /* measurement must never affect the page it measures */
  }
}

/**
 * The stamped outcome, read back for the beacon: the rung, and how long the page waited for sign-in (to the moment
 * it answered, else to now, the page's end), in whole ms. Null when nothing was stamped or the stamp is malformed.
 */
export function readAuthStall(): { rung: AuthStallRung; waitedMs: number } | null {
  try {
    const p = perf();
    const entry = p?.getEntriesByName?.(AUTH_STALL_MARK, 'mark')?.[0] as (PerformanceEntry & { detail?: unknown }) | undefined;
    const detail = entry?.detail as { rung?: unknown } | undefined;
    if (!detail || typeof detail.rung !== 'string' || !(AUTH_STALL_RUNGS as readonly string[]).includes(detail.rung)) {
      return null;
    }
    const answered = p?.getEntriesByName?.(AUTH_ANSWERED_MARK, 'mark')?.[0];
    const end = answered && Number.isFinite(answered.startTime) ? answered.startTime : p?.now?.() ?? 0;
    const waitedMs = Math.min(AUTH_STALL_MAX_MS, Math.max(0, Math.round(end)));
    return { rung: detail.rung as AuthStallRung, waitedMs };
  } catch {
    return null;
  }
}
